import React, { Component, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { when } from 'mobx';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import Loading from 'react-simple-loading';
import Firebase from 'firebase';

import { translate } from 'services/i18nService';
import AccessLevelService, { goldPromo, free } from 'services/AccessLevelService';
import CookieStorageService from 'services/CookieStorageService';
import fanClubService from 'services/fanClub';
import FirebaseService from 'services/FirebaseService';
import messagingService from 'services/MessagingService/MessagingService';
import {
  COMPONENT_MOUNT,
  COMPONENT_UNMOUNT,
  PRIVATE_SHOW_REQUEST_EXPIRED,
  PRIVATE_SHOW_REQUEST_SENT,
  REINIT_ROOM,
  SET_GENDER_PREFERENCE,
} from 'services/MessagingService/EventNames';
import MetaService from 'services/MetaService';
import ModalService from 'services/ModalService';
import VideoAdService from 'services/adServices/VideoAdService';
import AdStore from 'stores/AdStore';
import ChatStore from 'stores/ChatStore';
import FanClubStore from 'stores/FanClubStore';
import FirebaseStore from 'stores/FirebaseStore';
import GamesStore from 'stores/GamesStore';
import GiftsStore from 'stores/GiftsStore';
import PaymentStore from 'stores/PaymentStore';
import ProfileStore from 'stores/ProfileStore';
import TippingStore from 'stores/TippingStore';
import UserStore from 'stores/UserStore';
import TrackCam2CamProfile from 'pages/ProfileV2/TrackCam2CamProfile/TrackCam2CamProfile';
import { BROADCAST_STATUS } from 'enum/videoPlayer';
import APIStates from 'enum/APIStates';
import {
  FORCE_PROFILE_V2_REFRESH,
  USER_MODALS_GROUP,
} from 'enum/helpers';
import routeDefinitions from 'enum/routeDefinitions';
import { MINUTE_AS_MILLISECONDS } from 'enum/timeFormat';
import allUserGroups from 'enum/userGroups';
import { getGenderStr } from 'utils/gender';
import { isMobileRender } from 'utils/helpers';
import {
  actionCreators as paymentActions,
  selector as paymentSelector,
} from 'features/Payment/State';
import {
  actionCreators as payoutActions,
  selector as payoutSelector,
} from 'features/Payout/State';

import firebaseNode from '../../firebase/firebaseNode';

import { selector as profileSelector } from './State';


const ProfileV2Desktop = lazy(() => import('./ProfileV2Desktop/ProfileV2Desktop'));
const ProfileV2Mobile = lazy(() => import('./ProfileV2Mobile/ProfileV2Mobile'));

const CANCEL_PS_EXPIRED_DELAY = 2 * MINUTE_AS_MILLISECONDS;

@withRouter
@connect(profileSelector)
@connect(payoutSelector, (dispatch) => {
  return { payoutActions: bindActionCreators(payoutActions, dispatch) };
})
@connect(paymentSelector, (dispatch) => {
  return { paymentActions: bindActionCreators(paymentActions, dispatch) };
})
@observer
class ProfileV2 extends Component {
  static propTypes = {
    match: PropTypes.object.isRequired,
    paymentActions: PropTypes.object.isRequired,
    embedProfileV2: PropTypes.bool,
    history: PropTypes.object,
    user: PropTypes.object,
  };

  constructor(...args) {
    super(...args);

    this.metaService = new MetaService();
    this.messagingService = messagingService();
    this.privateShowRequestCounter = null;
    this.refreshTimer = null;
  }

  state = { shouldRenderMobile: isMobileRender() };

  componentDidMount() {
    const { hasCF, user, isLoggedIn, hasUserModal } = UserStore;
    const { match } = this.props;
    const performerName = match.params.user;
    const shouldShowFeedbackModal =
      isLoggedIn &&
      AccessLevelService.isMin(free, this.props.user.accessLevel) &&
      !AccessLevelService.isMin(goldPromo, user.accessLevel) &&
      hasCF(allUserGroups.PROFILES_V2_FEEDBACK_OVERLAY) &&
      !hasUserModal(USER_MODALS_GROUP.PROFILE_FEEDBACK);
    const shouldShowReffererModal = !isLoggedIn && location.search && location.search.includes('showSignupPopup');
    window.addEventListener('resize', this.handleMobileOrDesktop);
    window.addEventListener('keydown', this.handleVideoMetrics);
    if (hasCF(allUserGroups.PROFILE_V2_FORCE_REFRESH)) {
      window.addEventListener('focus', this.onFocus);
      window.addEventListener('blur', this.onBlur);
    }
    if (shouldShowFeedbackModal) {
      ModalService.showModal(
        ModalService.MODAL_KEYS.PROFILE_V2_FEEDBACK_MODAL,
        { close: (actionName) => this.handleCloseFeedbackModal(actionName) }
      );
    }
    if (shouldShowReffererModal) {
      ProfileStore.showRegistrationModal();
    }
    ProfileStore.getPerformerInfo(performerName);
    when(
      () => ProfileStore.profileExists,
      () => this.initialSetup()
    );
    if (!user.username) {
      this.setGenderPreference();
    }
    this.messagingService.emit(COMPONENT_MOUNT, { component: 'ProfileV2' });
    if (window.location.pathname === `/${performerName.concat(routeDefinitions.checkoutTokens)}`) {
      this.showBuyTokensModal();
    }
  }

  componentDidUpdate(prevProps) {
    const performerName = this.props.match.params.user;
    const prevPerformerName = prevProps.match.params.user;
    const roomName = performerName.toLowerCase();
    const prevRoomName = prevPerformerName.toLowerCase();
    const prevUsername = prevProps.user.username;
    const { username, accessHash } = this.props.user;
    const isPerfomerWatchingOwnProfile = username && username.toLowerCase() === roomName;
    const { hasCF, hasUserModal } = UserStore;
    const shouldShowFeedbackModal =
      username &&
      AccessLevelService.isMin(free, this.props.user.accessLevel) &&
      !AccessLevelService.isMin(goldPromo, this.props.user.accessLevel) &&
      hasCF(allUserGroups.PROFILES_V2_FEEDBACK_OVERLAY) &&
      !hasUserModal(USER_MODALS_GROUP.PROFILE_FEEDBACK);

    if (Boolean(prevUsername) && Boolean(username) === false) {
      Firebase.goOffline();
      ChatStore.togglePrivateUnreadMessagesIndicator(false);
      ChatStore.togglePrivateMessages(false);
      ChatStore.resetChatSettings();
    }

    if (prevRoomName !== roomName) {
      this.cleanStoresAndFirebase(prevRoomName);
      this.initialSetup();
    }

    if (prevUsername !== username) {
      if (prevUsername === null) {
        const connectedRef =
          FirebaseService.FirebaseMain && FirebaseService.FirebaseMain.child(firebaseNode.CONNECTED_REF);
        connectedRef?.off?.();
        Firebase.goOffline();
      }
      if (
        !UserStore.hasCF(allUserGroups.BROADCASTER_ONLINE_PROFILE_V2) ||
        (UserStore.hasCF(allUserGroups.BROADCASTER_ONLINE_PROFILE_V2) &&
          ProfileStore.performerOnline &&
          !isPerfomerWatchingOwnProfile)
      ) {
        FirebaseStore.initRoom(roomName, username, accessHash);
      }
      FanClubStore.getPerformerFanClubInfo(performerName);
      if (shouldShowFeedbackModal) {
        ModalService.showModal(
          ModalService.MODAL_KEYS.PROFILE_V2_FEEDBACK_MODAL,
          { close: (actionName) => this.handleCloseFeedbackModal(actionName) }
        );
      }
      if (username) {
        ProfileStore.getSilenceList(username);
        ProfileStore.getLoggedInUserInfo(username);
        PaymentStore.getAutoBillingStatus(username);
        GiftsStore.getGoldGifts();
      } else {
        PaymentStore.resetAutoBillingData();
      }
      ChatStore.getChatSettings(username);
      ChatStore.getChatSettings(ProfileStore.profileName, true);
      if (!VideoAdService.shouldDisplayVideoAds(this.props.user, ProfileStore.broadcastStatus)) {
        VideoAdService.destroy();
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleMobileOrDesktop);
    window.removeEventListener('keydown', this.handleVideoMetrics);
    this.cleanStoresAndFirebase(this.props.match.params.user.toLowerCase());
    ProfileStore.setPrivateShowReady(false);

    this.messagingService.off(REINIT_ROOM, this.reInitFirebase);

    // WARNING: this off missed in actual implementation, but will not be fixed as part of this MR
    // this.messagingService.off(PRIVATE_SHOW_REQUEST_SENT, this.handlePrivateShowRequestCounter);

    this.messagingService.emit(COMPONENT_UNMOUNT, { component: 'ProfileV2' });
    clearTimeout(this.refreshTimer);
    window.removeEventListener('focus', this.onFocus);
    window.removeEventListener('blur', this.onBlur);
  }

  initialSetup = () => {
    const { performerDetails, hasNewFanClub, profileInfo } = ProfileStore;

    this.metaService.setMetaContent(
      translate('v2profile.seo.main.description', { 0: performerDetails.username }),
      translate('v2profile.seo.main.title', { 0: performerDetails.username })
    );
    this.metaService.setCustomMeta({
      ogImage: performerDetails.profileImageUrl,
      ogUrl: window.location.href,
      twitterImage: performerDetails.profileImageUrl,
    });

    const { user: performerName } = this.props.match.params;
    const { user: { username, accessHash } } = this.props;
    const hasTemptedFanclubCF = UserStore.hasCF(allUserGroups.TEMPTED_FANCLUB);
    const profileName = profileInfo.username;
    ProfileStore.getSilenceList(username);
    if (hasNewFanClub && hasTemptedFanclubCF) {
      fanClubService.resetToken();
      fanClubService.public.fetchFanClub({ profileName });
    } else {
      FanClubStore.getPerformerFanClubInfo(performerName);
    }
    ProfileStore.getWishList(performerName);
    ProfileStore.getAwards(performerName);
    ChatStore.getChatSettings(username);
    ChatStore.getChatSettings(ProfileStore.profileName, true);
    GamesStore.init();
    if (!UserStore.hasCF(allUserGroups.BROADCASTER_ONLINE_PROFILE_V2)) {
      const roomName = performerName.toLowerCase();
      FirebaseStore.initRoom(roomName, username, accessHash);
    }
    this.messagingService.on(REINIT_ROOM, this.reInitFirebase);
    this.messagingService.on(PRIVATE_SHOW_REQUEST_SENT, this.handlePrivateShowRequestCounter);
    if (VideoAdService.shouldDisplayVideoAds(this.props.user, ProfileStore.broadcastStatus)) {
      VideoAdService.importIMASDK();
    }
    if (username) {
      ProfileStore.getLoggedInUserInfo(username);
      PaymentStore.getAutoBillingStatus(username);
    }
  };

  cleanStoresAndFirebase = (name) => {
    FirebaseStore.removeFirebaseListeners(name);
    ChatStore.removeChatListeners(name, this.props.user.username);
    ChatStore.resetChatStoreState();
    TippingStore.resetTippingStoreState();
    FirebaseStore.resetFirebaseStoreState();
    FanClubStore.resetFanClubStoreState();
    ProfileStore.resetChatStoreState();
    GamesStore.destructor();
    this.messagingService.off(PRIVATE_SHOW_REQUEST_SENT, this.handlePrivateShowRequestCounter);
  };

  showBuyTokensModal = () => {
    const { paymentActions } = this.props;
    paymentActions.showBuyTokensModal();
  };

  reInitFirebase = ({ roomName, username, accessHash }) => {
    FirebaseStore.removeFirebaseListeners(roomName);
    FirebaseStore.initRoom(roomName, username, accessHash);
  };

  handleCloseFeedbackModal = (actionName) => {
    const { addNewUserModal, user } = UserStore;
    const { handleSendProfileFeedbackResponseStatus } = ProfileStore;
    const data = {
      username: user.username,
      modalType: USER_MODALS_GROUP.PROFILE_FEEDBACK,
      action: actionName,
    };
    addNewUserModal(data);
    handleSendProfileFeedbackResponseStatus(APIStates.INITIAL);
    ModalService.hideModal();
  };

  handlePrivateShowRequestCounter = () => {
    const { isPrivateShowRequestActive, togglePrivateShowRequest } = FirebaseStore;
    clearTimeout(this.privateShowRequestCounter);
    // cancel ps request afer 2 minutes
    this.privateShowRequestCounter = setTimeout(() => {
      if (isPrivateShowRequestActive) {
        togglePrivateShowRequest(false);
      }
      this.messagingService.emit(PRIVATE_SHOW_REQUEST_EXPIRED);
    }, CANCEL_PS_EXPIRED_DELAY);
  };

  handleMobileOrDesktop = () => {
    if (this.state.shouldRenderMobile !== isMobileRender()) {
      this.setState({ shouldRenderMobile: isMobileRender() });
      ProfileStore.resetPortraitFullScreen();
    }
  };

  handleVideoMetrics = (e) => {
    const { setVideoStatsVisibility, isVideoStatsVisible } = ProfileStore;
    if (e.ctrlKey && e.shiftKey && (e.key === 'f' || e.key === 'F')) {
      setVideoStatsVisibility(!isVideoStatsVisible);
    }
  };

  handleRenderMobileOrDesktop() {
    const { embedProfileV2 } = this.props;
    if (ProfileStore.performerInfoState === (APIStates.PENDING || APIStates.INITIAL) && !ProfileStore.hasProfileInfo) {
      return <Loading />;
    }
    if (ProfileStore.performerInfoState === APIStates.ERROR_404) {
      this.props.history.push(routeDefinitions.pageNotFound);
    } else if (ProfileStore.performerInfoState === APIStates.ERROR_401) {
      this.props.history.push(routeDefinitions.root);
    } else if (ProfileStore.performerInfoState === APIStates.ERROR_403) {
      this.props.history.push(routeDefinitions.noProfile);
    }
    AdStore.setGenderType(ProfileStore.profileInfo.gender);

    return (
      <Suspense fallback={<Loading />}>
        {this.state.shouldRenderMobile ? (
          <ProfileV2Mobile
            {...this.props}
            embedProfileV2={embedProfileV2}
            promptRegistrationModal={FirebaseStore.online}
            spyRequestInProgress={ProfileStore.spyRequestInProgress}
          />
        ) : (
          <ProfileV2Desktop {...this.props} embedProfileV2={embedProfileV2} />
        )}
      </Suspense>
    );
  }

  setGenderPreference = () => {
    if (CookieStorageService.get('cam4-CONTENT')) {
      const contentPref = CookieStorageService.get('cam4-CONTENT');
      const preference = getGenderStr(contentPref);
      this.messagingService.emit(SET_GENDER_PREFERENCE, preference);
    }
  };

  onFocus = () => {
    clearTimeout(this.refreshTimer);
  };

  onBlur = () => {
    when(
      () => ProfileStore.broadcastStatus === BROADCAST_STATUS.DISCONNECTED && UserStore.isNewVersion,
      () => {
        this.refreshTimer = setTimeout(() => {
          window.location.reload();
        }, FORCE_PROFILE_V2_REFRESH);
      }
    );
  };

  render() {
    const { shouldNoIndexFollow } = ProfileStore;
    if (shouldNoIndexFollow) {
      this.metaService.setMeta('ROBOTS', 'NOINDEX, FOLLOW');
    }

    return (
      <React.Fragment>
        {this.handleRenderMobileOrDesktop()}
        <TrackCam2CamProfile />
      </React.Fragment>
    );
  }
}

export default ProfileV2;
